import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import OclLogo from './OclLogo';
import SecondaryButton from '../common/SecondaryButton';
import UserMenu from '../auth/UserMenu';
import { MobileQuickLinks, TrayButton } from './Tray';
import { LIGHT_GRAY } from '../../common/colors';
import { CHAT_URL, DOCS_URL } from '../../common/constants';
import { appLink } from '../../common/appLinks';
import { isActiveNavPath } from '../../common/nav';

// Nav per the Webflow design (Tools, Blog, Chat, Docs + OCL Online / Sign
// in), plus About — ocl_online#144: /about-us was reachable only from the
// footer, so a first-time visitor had to scroll the whole page to find out
// who OCL is. FAQ and Contact stay footer-only by decision.
//
// Internal routes first, then the two external destinations. Its own
// nav.about key rather than the footer's about_us: the header label is the
// short form ("About"), matching the one-word register of its neighbours,
// while the footer keeps the fuller "About us".
//
// Pricing sits beside it (ocl_online#126).
const navLinks = t => [
  { label: t('nav.tools'), to: '/tools' },
  { label: t('nav.pricing'), to: '/pricing' },
  { label: t('nav.blog'), to: '/blog' },
  { label: t('nav.about'), to: '/about-us' },
  { label: t('nav.chat'), href: CHAT_URL },
  { label: t('nav.docs'), href: DOCS_URL },
];

const linkProps = link => link.to
  ? { component: RouterLink, to: link.to }
  : { component: 'a', href: link.href, target: '_blank', rel: 'noopener noreferrer' };

const NavButtons = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  return (
    <Stack direction="row" spacing={0.5}>
      {navLinks(t).map(link => {
        const active = isActiveNavPath(pathname, link.to);
        return (
          <Button
            key={link.label}
            {...linkProps(link)}
            aria-current={active ? 'page' : undefined}
            sx={{
              color: active ? 'primary.main' : 'surface.dark',
              fontWeight: active ? 700 : 500,
              // Six items now (ocl_online#126 added Pricing and About), so
              // the row is tighter and a shade smaller than the four-item
              // Webflow original to keep it clear of the OCL Online button.
              fontSize: '0.875rem',
              px: 1.25,
              whiteSpace: 'nowrap',
              // Underline sits on the AppBar's own bottom border, so the
              // selected tab reads as attached to the page below it.
              position: 'relative',
              '&::after': active ? {
                content: '""',
                position: 'absolute',
                left: 8,
                right: 8,
                bottom: -1,
                height: '2px',
                borderRadius: '2px 2px 0 0',
                backgroundColor: 'primary.main',
              } : undefined,
            }}>
            {link.label}
          </Button>
        );
      })}
    </Stack>
  );
};

const OclOnlineButton = ({ fullWidth }) => {
  const { t } = useTranslation();
  return (
    <SecondaryButton href={appLink('termBrowser')} fullWidth={fullWidth} startIcon={<ManageSearchIcon />}>
      {t('nav.ocl_online')}
    </SecondaryButton>
  );
};

const MobileMenu = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: 300 } } }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <IconButton aria-label={t('nav.close_menu')} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <MobileQuickLinks onClick={onClose} />
      <List component="nav" aria-label={t('nav.primary_aria')} onClick={onClose}>
        {navLinks(t).map(link => {
          const active = isActiveNavPath(pathname, link.to);
          return (
            <ListItemButton
              key={link.label}
              {...linkProps(link)}
              selected={active}
              aria-current={active ? 'page' : undefined}>
              <ListItemText
                primary={link.label}
                slotProps={{ primary: { sx: { fontWeight: active ? 700 : 500, color: active ? 'primary.main' : undefined } } }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Stack spacing={1.5} sx={{ p: 2 }}>
        <OclOnlineButton fullWidth />
      </Stack>
    </Drawer>
  );
};

const Header = () => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = React.useState(false);

  return (
    <AppBar position="sticky" elevation={0} sx={{ backgroundColor: 'background.paper', borderBottom: `1px solid ${LIGHT_GRAY}` }}>
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: { xs: 64, md: 72 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <TrayButton />
            </Box>
            <Box component={RouterLink} to="/" sx={{ display: 'inline-flex', textDecoration: 'none' }} aria-label={t('nav.home_aria')}>
              <OclLogo />
            </Box>
          </Box>
          <Box component="nav" aria-label={t('nav.primary_aria')} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            <NavButtons />
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <OclOnlineButton />
              <UserMenu />
            </Stack>
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            <UserMenu />
            <IconButton
              aria-label={t('nav.open_menu')}
              onClick={() => setMenuOpen(true)}
              sx={{ color: 'surface.dark' }}>
              <MenuIcon />
            </IconButton>
          </Box>
          <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
